import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Mail, Lock, User as UserIcon, AtSign, ArrowRight } from 'lucide-react';

const HERO_BG = 'https://images.unsplash.com/photo-1770795263316-f302a878ee64?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000';
const HERO_IMG = 'https://images.unsplash.com/photo-1585666176811-45c61b840ad4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC04"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export const Landing = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [form, setForm] = useState({ email: '', password: '', name: '', username: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'signup') {
        const { data } = await api.post('/auth/register', form);
        login(data.token, data.user);
        toast.success('Welcome to RAW MIXES');
        navigate('/dashboard');
      } else {
        const { data } = await api.post('/auth/login', { email: form.email, password: form.password });
        login(data.token, data.user);
        toast.success(`Welcome back, ${data.user.name}`);
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <div className="flex flex-col lg:flex-row flex-1">
      {/* Left: Brand / Hero */}
      <div
        className="relative lg:w-[55%] min-h-[40vh] lg:min-h-screen flex flex-col justify-between p-8 lg:p-14 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(5,5,5,0.85) 0%, rgba(5,5,5,0.6) 60%, rgba(255,176,0,0.15) 100%), url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black via-transparent to-[#FFB000]/10 pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <Logo size="md" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative z-10 max-w-xl"
        >
          <div className="inline-block px-3 py-1 rounded-full border border-[#FFB000]/30 bg-[#FFB000]/5 backdrop-blur mb-6">
            <span className="text-[#FFB000] text-xs font-bold tracking-[0.2em] uppercase">Exclusive · Original · RAW</span>
          </div>
          <h1 className="font-heading font-black tracking-tighter text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6">
            Where the <span className="text-[#FFB000]">beat</span><br />
            lives.
          </h1>
          <p className="text-[#A1A1AA] text-lg max-w-md leading-relaxed mb-8">
            Stream exclusive DJ mixes and original productions straight from RAW.
            Discover fresh sounds and take the party anywhere. Real mixes, real energy,
            no filler.
          </p>

          {/* Feature strip */}
          <ul className="space-y-3 mb-2" aria-label="Features">
            <li className="flex items-start gap-3">
              <span className="text-[#FFB000] mt-0.5" aria-hidden>🎚️</span>
              <div>
                <span className="text-white font-bold font-heading text-sm">Exclusive DJ mixes</span>
                <span className="text-[#A1A1AA] text-sm"> — full sets you won&apos;t find anywhere else.</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#FFB000] mt-0.5" aria-hidden>🎵</span>
              <div>
                <span className="text-white font-bold font-heading text-sm">Original productions</span>
                <span className="text-[#A1A1AA] text-sm"> — fresh tracks made in-house by RAW.</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#FFB000] mt-0.5" aria-hidden>▶️</span>
              <div>
                <span className="text-white font-bold font-heading text-sm">Stream anywhere</span>
                <span className="text-[#A1A1AA] text-sm"> — smooth playback on any device.</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#FFB000] mt-0.5" aria-hidden>🔔</span>
              <div>
                <span className="text-white font-bold font-heading text-sm">New drops</span>
                <span className="text-[#A1A1AA] text-sm"> — get notified the moment a new mix lands.</span>
              </div>
            </li>
          </ul>
        </motion.div>

        <div className="relative z-10 flex items-center gap-6 text-[#A1A1AA] text-xs tracking-[0.2em] uppercase font-bold">
          <span>Mixes</span>
          <span className="w-8 h-px bg-[#FFB000]/40"></span>
          <span>Originals</span>
          <span className="w-8 h-px bg-[#FFB000]/40"></span>
          <span>Fresh drops</span>
        </div>
      </div>

      {/* Right: Auth form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <h2 className="font-heading font-bold text-white text-3xl tracking-tight mb-2">
              {mode === 'signin' ? 'Press play on RAW' : 'Join RAW Mixes'}
            </h2>
            <p className="text-[#A1A1AA] text-sm">
              {mode === 'signin' ? 'Sign in to keep the session going.' : 'Create an account and start streaming your new favourite set.'}
            </p>
          </div>

          <button
            onClick={handleGoogle}
            className="w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-full bg-white text-black font-bold font-heading text-sm hover:bg-[#f3f3f3] transition-colors active:scale-[0.98]"
            data-testid="google-signin-btn"
          >
            <GoogleIcon />
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[#52525B] text-xs tracking-[0.2em] uppercase font-bold">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <>
                <div className="relative">
                  <UserIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
                  <input
                    type="text"
                    required
                    placeholder="Your name"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    className="input-default pl-12"
                    data-testid="signup-name"
                  />
                </div>
                <div className="relative">
                  <AtSign size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
                  <input
                    type="text"
                    required
                    placeholder="username"
                    value={form.username}
                    onChange={e => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                    className="input-default pl-12"
                    data-testid="signup-username"
                  />
                </div>
              </>
            )}
            <div className="relative">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
              <input
                type="email"
                required
                placeholder="you@domain.com"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="input-default pl-12"
                data-testid="auth-email"
              />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
              <input
                type="password"
                required
                minLength={6}
                placeholder="Password"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                className="input-default pl-12"
                data-testid="auth-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-pill btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="auth-submit"
            >
              {loading ? 'Loading...' : mode === 'signin' ? 'Sign in' : 'Create account'}
              {!loading && <ArrowRight size={16} />}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-[#A1A1AA]">
            {mode === 'signin' ? (
              <>
                New here?{' '}
                <button
                  onClick={() => setMode('signup')}
                  className="text-[#FFB000] hover:text-[#E69E00] font-bold"
                  data-testid="switch-signup"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already on the roster?{' '}
                <button
                  onClick={() => setMode('signin')}
                  className="text-[#FFB000] hover:text-[#E69E00] font-bold"
                  data-testid="switch-signin"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </motion.div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default Landing;
